<div *transloco="let t">
	<vex-page-layout mode="card">
		<vex-page-layout-header class="pb-16 flex flex-col justify-center items-start">
			<div>
				<h1 id="support-ara-app-title" class="title mt-0 mb-1">{{ t('supportAraApp.title') }}</h1>
				<vex-breadcrumbs [crumbs]="this.breadCrumbService.crumbs"></vex-breadcrumbs>
			</div>
		</vex-page-layout-header>
		<vex-page-layout-content vexContainer>
			<div class="card p-6">
				<div @stagger class="no-scroll">
					<div class="section">
						<h2 id="what-is-ara-app" class="headline">
							{{ t('supportAraApp.whatIsAraApp') }}
							<a [routerLink]="[]" fragment="what-is-ara-app" class="anchor-link">
								<mat-icon [svgIcon]="icons.get('link')"></mat-icon>
							</a>
						</h2>
						<p class="subtitle mb-4">{{ t('supportAraApp.araAppSubtitle') }}</p>
						<ul class="list-none">
							<li class="mb-2" [innerHTML]="t('supportAraApp.araAppDescription.first')"></li>
							<li class="mb-2">{{ t('supportAraApp.araAppDescription.second') }}</li>
						</ul>
					</div>

					<div class="section mt-6">
						<h2 id="important-functions" class="headline">
							{{ t('supportAraApp.importantFunctionsTitle') }}
							<a [routerLink]="[]" fragment="important-functions" class="anchor-link">
								<mat-icon [svgIcon]="icons.get('link')"></mat-icon>
							</a>
						</h2>
						<ul class="list-disc ml-6">
							@for (
								key of ['consumptionOverview', 'comparisonAverage', 'navigation', 'detailView', 'pdfExport', 'multiObjectManagement'];
								track key
							) {
								<li class="mb-2">
									<span class="item-label">{{ t('supportAraApp.importantFunctionsList.' + key + '.label') }}</span>
									{{ t('supportAraApp.importantFunctionsList.' + key + '.desc') }}
								</li>
							}
						</ul>
					</div>

					<div class="section mt-6">
						<h2 id="access" class="headline">
							{{ t('supportAraApp.accessTitle') }}
							<a [routerLink]="[]" fragment="access" class="anchor-link">
								<mat-icon [svgIcon]="icons.get('link')"></mat-icon>
							</a>
						</h2>
						<div class="mt-2">
							@if (sessionService.isDevRole()) {
								<div class="mb-4">
									<span class="item-label">{{ t('supportAraApp.nativeApps.android.title') }}</span>
									<!-- Check this for size changes: https://partnermarketinghub.withgoogle.com/brands/google-play/visual-identity/badge-guidelines/#design-guidelines -->
									<a href="https://play.google.com/store/apps/details?id=de.arasys.ara_app" target="_blank" rel="noopener noreferrer">
										<img
											[src]="t('supportAraApp.nativeApps.android.storeBadge.src')"
											[alt]="t('supportAraApp.nativeApps.android.storeBadge.alt')"
											[width]="160"
											class="mx-2 my-3" />
									</a>
									<span class="text-xs mx-2">{{ t('supportAraApp.nativeApps.android.legalLine') }}</span>
								</div>
								<div class="mb-4">
									<span class="item-label">{{ t('supportAraApp.nativeApps.iOs.title') }}</span>
									<ul class="list-disc ml-6 mt-2">
										<li>{{ t('supportAraApp.nativeApps.iOs.subTitle') }}</li>
									</ul>
								</div>
							}
							<div class="mb-4">
								<span class="item-label">{{ t('supportAraApp.webAppSubtitle') }}</span>
								<a href="https://ara-app.eu" target="_blank" mat-button color="primary">{{ t('supportAraApp.webAppUrl') }}</a>
								<ul class="list-none mt-2">
									<li>{{ t('supportAraApp.webAppDevices') }}</li>
									<li>{{ t('supportAraApp.webAppBrowsers') }}</li>
								</ul>
							</div>
						</div>
					</div>

					<div class="section mt-6">
						<h2 id="how-it-works" class="headline">
							{{ t('supportAraApp.howItWorksTitle') }}
							<a [routerLink]="[]" fragment="how-it-works" class="anchor-link">
								<mat-icon [svgIcon]="icons.get('link')"></mat-icon>
							</a>
						</h2>
						<div class="mt-2">
							@for (key of ['getAuthCode', 'registration', 'viewData']; track key) {
								<div class="mb-4">
									<span class="item-label">{{ t('supportAraApp.howItWorksList.' + key + '.label') }}</span>
									{{ t('supportAraApp.howItWorksList.' + key + '.desc') }}
								</div>
							}
						</div>
					</div>

					<div class="section mt-6">
						<h2 id="faq" class="headline">
							{{ t('supportAraApp.faqTitle') }}
							<a [routerLink]="[]" fragment="faq" class="anchor-link">
								<mat-icon [svgIcon]="icons.get('link')"></mat-icon>
							</a>
						</h2>

						<div class="faq-group mt-4">
							<h2 id="faq-login" class="text-lg font-medium flex items-center mb-3">
								<mat-icon [svgIcon]="icons.get('vpnKey')" color="primary" class="mr-2 icon-sm"></mat-icon>
								{{ t('supportAraApp.loginTitle') }}
							</h2>
							<mat-accordion>
								@for (key of ['getAuthCode', 'cannotLogin', 'registrationOrLogin']; track key) {
									<mat-expansion-panel class="mb-2">
										<mat-expansion-panel-header>
											<mat-panel-title>
												{{ t('supportAraApp.loginList.' + key + '.label') }}
											</mat-panel-title>
										</mat-expansion-panel-header>
										<p>{{ t('supportAraApp.loginList.' + key + '.desc') }}</p>
									</mat-expansion-panel>
								}
							</mat-accordion>
						</div>

						<div class="faq-group mt-8">
							<h2 id="faq-consumption-data" class="text-lg font-medium flex items-center mb-3">
								<mat-icon [svgIcon]="icons.get('consumptionInfo')" color="primary" class="mr-2 icon-sm"></mat-icon>
								{{ t('supportAraApp.consumptionDataTitle') }}
							</h2>
							<mat-accordion>
								@for (key of ['whereToFind', 'whatIsUVI', 'moreThanAverage']; track key) {
									<mat-expansion-panel class="mb-2">
										<mat-expansion-panel-header>
											<mat-panel-title>
												{{ t('supportAraApp.consumptionDataList.' + key + '.label') }}
											</mat-panel-title>
										</mat-expansion-panel-header>
										<p>{{ t('supportAraApp.consumptionDataList.' + key + '.desc') }}</p>
									</mat-expansion-panel>
								}
							</mat-accordion>
						</div>

						<div class="faq-group mt-8">
							<h2 id="faq-technical-questions" class="text-lg font-medium flex items-center mb-3">
								<mat-icon [svgIcon]="icons.get('settings')" color="primary" class="mr-2 icon-sm"></mat-icon>
								{{ t('supportAraApp.technicalQuestionsTitle') }}
							</h2>
							<mat-accordion>
								@for (key of ['supportedBrowsers', 'smartphoneApp', 'saveAsPdf', 'loginPopup']; track key) {
									<mat-expansion-panel class="mb-2">
										<mat-expansion-panel-header>
											<mat-panel-title>
												{{ t('supportAraApp.technicalQuestionsList.' + key + '.label') }}
											</mat-panel-title>
										</mat-expansion-panel-header>
										<p>{{ t('supportAraApp.technicalQuestionsList.' + key + '.desc') }}</p>
									</mat-expansion-panel>
								}
							</mat-accordion>
						</div>
					</div>
				</div>
			</div>
		</vex-page-layout-content>
	</vex-page-layout>
</div>
